import type { NativeSelectOption } from '@shared/ui/fields'

import { Type as BookingType } from '@brighterly/lib-core-types/booking/enums'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { useLessonCreateStore } from './create/store'
import { useLessonCreateSave } from './lesson-create-save.hook'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { buildLessonTypeOptions, getAllowedLessonTypes } from './lesson-create'

interface LessonCreateSingleReturn {
  refCanCreate: ComputedRef<boolean>
  refTypeOptions: ComputedRef<NativeSelectOption[]>
  refType: ComputedRef<BookingType | null>
  refBalance: ComputedRef<number | null>
  refIsBalanceShown: ComputedRef<boolean>
  refDate: ComputedRef<string>
  refMinDate: ComputedRef<string>
  refStartOptions: ComputedRef<NativeSelectOption[]>
  refStartTime: ComputedRef<string>
  refDurationOptions: ComputedRef<NativeSelectOption[]>
  refDuration: ComputedRef<number | null>
  refCanChangeTutor: ComputedRef<boolean>
  refIsSaving: ComputedRef<boolean>
  refLessonsUrl: string
  setType: (option: NativeSelectOption) => void
  setDate: (value: string) => void
  setStartTime: (option: NativeSelectOption) => void
  setDuration: (option: NativeSelectOption) => void
  openTutorModal: () => void
  openBalance: () => Promise<void>
  submit: () => Promise<void>
}

/**
 * The create form: type, date and time, with the Tutor block beside them. Nothing here asks the API: the tutor is
 * picked in the tutor modal, which reads the tutors for the chosen time and the filters set there, and adds the
 * lesson from there, as the edit page's modal saves.
 */
export const useLessonCreateSingle = (): LessonCreateSingleReturn => {
  const route = useRoute()
  const store = useLessonCreateStore()
  const listsStore = useListsStore()
  const { canManageBookings, canManageDemoBookings, canViewTutorsSchedule } = useLessonPrivileges()
  const { refLessonsUrl, refBalance, warnWhenNoBalance, createLesson } = useLessonCreateSave()

  const refAllowedTypes = computed<BookingType[]>(() => getAllowedLessonTypes({
    canManageBookings: canManageBookings.value,
    canManageDemoBookings: canManageDemoBookings.value,
  }))

  const refCanCreate = computed<boolean>(() => refAllowedTypes.value.length > 0)

  const refType = computed<BookingType | null>(() => store.getType)

  const refTypeOptions = computed<NativeSelectOption[]>(() =>
    buildLessonTypeOptions(listsStore.getLists?.booking.type ?? {}, refAllowedTypes.value, refType.value))

  const refIsBalanceShown = computed<boolean>(() => refType.value === BookingType.Paid && refBalance.value !== null)

  const balanceUrl = resolveUrl(AppRoute.DisciplinesBalances, { id: route.params.id as string }) ?? ''

  const refDate = computed<string>(() => store.getDate)

  const refMinDate = computed<string>(() => store.getToday)

  const refStartTime = computed<string>(() => store.getStartTime)

  const refStartOptions = computed<NativeSelectOption[]>(() =>
    (store.getSlots?.start ?? []).map(slot => ({
      id: slot,
      value: slot,
      text: slot,
      selected: slot === refStartTime.value,
    })))

  const refDuration = computed<number | null>(() => store.getDuration)

  const refDurationOptions = computed<NativeSelectOption[]>(() =>
    (store.getSlots?.duration ?? []).map(minutes => ({
      id: minutes,
      value: minutes,
      text: `${minutes} min`,
      selected: minutes === refDuration.value,
    })))

  const refIsSaving = computed<boolean>(() => store.isSaving)

  /** Picking a tutor needs the tutors' schedules; without that right the lesson is added without one, as the legacy
   *  form did with its disabled select. */
  const refCanChangeTutor = computed<boolean>(() => refCanCreate.value && canViewTutorsSchedule.value && !refIsSaving.value)

  const setType = (option: NativeSelectOption): void => {
    store.setType(option.value as BookingType)
  }

  /** Clearing the date falls back to today, as the legacy picker did. */
  const setDate = (value: string): void => {
    store.setDate(value || store.getToday)
  }

  const setStartTime = (option: NativeSelectOption): void => {
    store.setStartTime(String(option.value))
  }

  const setDuration = (option: NativeSelectOption): void => {
    store.setDuration(Number(option.value))
  }

  const openTutorModal = (): void => {
    if (!refCanChangeTutor.value) return

    store.setTutorModalOpen(true)
  }

  /** Where the balance is topped up: the discipline's Balance tab. */
  const openBalance = async (): Promise<void> => {
    await navigateTo(balanceUrl)
  }

  const submit = async (): Promise<void> => {
    if (refIsSaving.value || !store.getStart || warnWhenNoBalance()) return

    if (refCanChangeTutor.value) {
      openTutorModal()
      return
    }

    await createLesson()
  }

  return {
    refCanCreate,
    refTypeOptions,
    refType,
    refBalance,
    refIsBalanceShown,
    refDate,
    refMinDate,
    refStartOptions,
    refStartTime,
    refDurationOptions,
    refDuration,
    refCanChangeTutor,
    refIsSaving,
    refLessonsUrl,
    setType,
    setDate,
    setStartTime,
    setDuration,
    openTutorModal,
    openBalance,
    submit,
  }
}
